import React from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as Sentry from '@sentry/browser';
import * as actionCreators from 'actions/conversation';
import { updateSelfEvents } from 'actions/index';
import { userData, events as eventsSelector, eventsLoading } from 'selectors/user';
import { list as tagsList, tagsAlreadyFetched } from 'modules/tags/redux/selectors';
import { postTagRequest, fetchTagsRequest } from 'modules/tags/redux/actionCreators';
import { DASHBOARD_REQUEST_REVIEW_NOTIFICATION } from 'constants/api';
import Api from 'api';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import { isDeletedUser } from '../conversationLock';
import MessageTab from '../components/MessageTab';
import ConversationTabs from '../components/ConversationTabs';
import ShowMoreButton from '../components/ShowMoreButton';
import TabRightMenu from '../components/TabRightMenu';

const View = () => {
  const intl = useIntl();
  const [isTagOpen, setIsTagOpen] = React.useState(false);
  const [cottagesToAskForReview, setCottagesToAskForReview] = React.useState([]);
  const [askReviewDialogOpen, setAskReviewDialogOpen] = React.useState(false);
  const { conversationId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector(userData);
  const tags = useSelector(tagsList);
  const tagsFetched = useSelector(tagsAlreadyFetched);
  const events = useSelector(eventsSelector);
  const isLoadingEvents = useSelector(eventsLoading);

  const { isFetching, error, success, isPosting, data } = useSelector(
    (state) => state.conversation,
  );

  const canAskForReview = async () => {
    try {
      const results = await Api.fetchConversationCanAskForReview(conversationId);

      setCottagesToAskForReview(results);
    } catch (err) {
      Sentry.captureException(err);
    }
  };

  React.useEffect(() => {
    if (!tagsFetched) {
      dispatch(fetchTagsRequest());
    }

    if (!isFetching) {
      dispatch(actionCreators.fetchConversationRequest({ conversationId }));

      canAskForReview();
    }

    return () => {
      dispatch(actionCreators.clearConversation());
    };
  }, []);

  const checkMessageSeen = () => {
    const msgs = data.Messages;
    const userId = currentUser.id;

    if (msgs.length === 0) {
      return;
    }

    const firstUnread = msgs.find(
      (msg) => msg.user_id !== userId && !msg.seen_by_users.includes(userId),
    );

    if (!firstUnread) {
      return;
    }

    dispatch(
      actionCreators.postMessageSeenRequest({
        conversationId: firstUnread.conversation_id,
        messageId: firstUnread.id,
      }),
    );
  };

  React.useEffect(() => {
    if (data && !isEmpty(data)) {
      checkMessageSeen();
    }
  }, [data]);

  const askForReviewNotificationClick = () => {
    dispatch(
      updateSelfEvents({
        events: [DASHBOARD_REQUEST_REVIEW_NOTIFICATION],
      }),
    );
  };

  const askForReview = async (body) => {
    await Api.postAskForReviewMessage(conversationId, body);

    setCottagesToAskForReview(
      cottagesToAskForReview.filter((c) => c.id !== parseInt(body.cottageId, 10)),
    );

    dispatch(actionCreators.fetchConversationRequest({ conversationId }));
  };

  const changeTags = (_tags, showArchivedMsg) => {
    const newTag = _tags.find((tag) => typeof tag.value === 'string');
    const { id } = data;

    if (newTag) {
      dispatch(postTagRequest({ id, tag: newTag.value, tags: _tags }));
    } else {
      dispatch(actionCreators.postConversationTagsRequest({ id, tags: _tags, showArchivedMsg }));
    }
  };

  const toggleArchived = () => {
    let newTags = data.tags.filter(({ id }) => id !== 3);

    if (newTags.length === data.tags.length) {
      const archived = tags.find(({ id }) => id === 3);
      newTags = newTags.concat([archived]);
    }

    changeTags(
      newTags.map(({ id, tag }) => ({ value: id, label: tag })),
      true,
    );
  };

  const reviewHost = async (_data) => {
    const res = await Api.postHostReview(conversationId, _data);

    dispatch(actionCreators.updateConversationReview(res));
  };

  const shouldOpenAskReviewNotification =
    !isLoadingEvents && !events[DASHBOARD_REQUEST_REVIEW_NOTIFICATION];
  // A failed fetch (missing conversation / no access) sets `error` and leaves
  // `data` empty. Without this branch the empty data would spin the loader
  // forever -- instead we stop loading and surface a message in MessageTab.
  const hasError = !isEmpty(error);
  const hasData = !isEmpty(data);
  const isLoading = isFetching || isEmpty(currentUser) || (!hasData && !hasError);
  const showLoader = isLoading;
  const loaderProps = { showLoader, hasError, isPosting };
  const isHost = currentUser.has_reviewed_cottages;

  let displayName = '';
  let otherUsers = [];

  // Only when the conversation actually loaded -- on error `data` is still empty.
  if (hasData) {
    otherUsers = (Array.isArray(data.Users) ? data.Users : []).filter(
      (user) => user.id !== currentUser.id,
    );
    displayName = otherUsers
      .map((user) =>
        isDeletedUser(user)
          ? intl.formatMessage({ id: 'DeletedUser', defaultMessage: 'Deleted user' })
          : user.name,
      )
      .join(', ');
  }

  // Prefer the loaded name; until the conversation resolves, fall back to the
  // counterpart name passed from the list (via Link state) so the AppBar title
  // doesn't pop in. Direct URL/refresh has no state -> stays blank until load.
  const title = displayName || location.state?.counterpartName || '';

  return (
    <Main
      title={title}
      extra={
        data && data?.filters?.commission ? null : (
          <ShowMoreButton data={data} isFetching={isFetching} currentUser={currentUser} />
        )
      }
    >
      {/* Render the tab bar during loading too -- it only needs the route
          param, and reserving its ~48px keeps MessageTab's fixed dvh height
          from jumping down when data arrives and the bar would otherwise pop
          in. The right-hand menu needs data, so it fills in once loaded.
          Mirror MessageTab's body: show tabs with the spinner or the
          conversation, hide them only when it shows the standalone error
          (`!showLoader && hasError`) -- so the bar also stays put through a
          post/tag failure that keeps the loaded conversation on screen. */}
      {(showLoader || !hasError) && (
        <ConversationTabs
          tab={1}
          rightMenu={
            hasData ? (
              <TabRightMenu
                {...{
                  currentUser,
                  otherUsers,
                  cottagesToAskForReview,
                  isHost,
                  shouldOpenAskReviewNotification,
                  askForReviewNotificationClick,
                  askReviewDialogOpen,
                  setAskReviewDialogOpen,
                  askForReview,
                  tags,
                  toggleArchived,
                  data,
                  isTagOpen,
                  setIsTagOpen,
                  changeTags,
                  reviewHost,
                }}
              />
            ) : null
          }
        />
      )}
      <MessageTab
        {...loaderProps}
        {...{
          data,
          isHost,
          conversationId,
          currentUser,
          showNoteMessage: !isHost && data?.filters?.commission === true,
        }}
        deleteMessage={(p) => dispatch(actionCreators.deleteMessageRequest(p))}
      />
      <Success success={success} onClose={() => dispatch(actionCreators.clearConversation())} />
    </Main>
  );
};

export default View;
